import { createContext, useContext, useState } from 'react';
import { SessionState, LoadSessionViewModel } from './SessionState';
import { useLocation, useNavigate } from 'react-router-dom';
import { APIContext } from './APIContext';
import dayjs from 'dayjs';
import React from 'react';
import { DotNestedKeys } from '../Utilities/Types/SessionType';

type ContextModel = {
    saveSession: (sessionName?: string, type?: 'manual' | 'audit' | 'Error', callback?: (auditCode: string) => void) => void;
    loadSession: (sessionId: string) => void;
    sessionState: SessionState;
    updateSessionState: (properties: Partial<SessionState>, callback?: (newState?: SessionState) => void) => void;
    setSessionField: <K extends DotNestedKeys<SessionState>>(
        path: K,
        value: unknown
    ) => void;
};

export const SessionContext = createContext<ContextModel>({
    saveSession: () => { throw Error('Cannot use a context without a provider'); },
    loadSession: () => { throw Error('Cannot use a context without a provider'); },
    sessionState: new SessionState(),
    updateSessionState: () => { throw Error('Cannot use a context without a provider'); },
    setSessionField: () => { throw Error('Cannot use a context without a provider'); },
});

export default function SessionContextProvider({ children }: React.PropsWithChildren<{}>) {
    const navigate = useNavigate();
    const location = useLocation();
    const { post, get, showMessage, version, setVersion } = useContext(APIContext);
    const [sessionState, setSessionState] = useState<SessionState>(new SessionState());

    const saveSession = async (sessionName?: string, type?: string, callback?: (auditCode: string) => void) => {
        const versioned: SessionState = { ...sessionState, version };
        const savedSession = {
            url: location.pathname + location.search,
            state: JSON.stringify(versioned),
            name: sessionName ?? `Session_${new Date().toISOString()}`,
            type: type ?? 'Manual',
        };
        post('Session', 'SaveSession', savedSession, callback);
    }

    const loadSession = async (sessionId: string) => {
        get('Session', `LoadSession?sessionId=${sessionId}`, (data: LoadSessionViewModel) => {
            if (data.state !== undefined) {
                var val = JSON.parse(data.state);

                if (val.landusePage != undefined) {
                    var start = val.landusePage?.startDate ?? val.primaryFilters?.startDate;
                    var end = val.landusePage?.endDate ?? val.primaryFilters?.endDate;

                    var sdate = dayjs(start)
                    var edate = dayjs(end)

                    val.landusePage.startDate = sdate;
                    val.landusePage.endDate = edate;
                }
                else {
                    var start2 = val.primaryFilters.startDate;
                    var end2 = val.primaryFilters.endDate;

                    var sdate = dayjs(start2)
                    var edate = dayjs(end2)
                    val.landusePage = {
                        startDate: sdate,
                        endDate: edate,
                        serviceVehicles: val.primaryFilters.serviceVehicles,
                        sites: [],
                        isMultiModal: val.landUseAndTripRateFilters.isMultiModal,
                    }
                }

                setVersion(val.version ?? 0);
                setSessionState(val);
                const separator = data.url.includes('?') ? '&' : '?';
                navigate(`${data.url}${separator}fromSession=true`);
               
            } else {
                showMessage("error", "This audit id does not exist");
            }
        });
    }

    const updateSessionState = async (properties: Partial<SessionState>, callback?: (newState?: SessionState) => void) => {
        setSessionState(prev => {
            const newState = { ...prev, ...properties };
            if (callback) {
                callback(newState);
            }
            return newState;
        });
    }

    const setSessionField = <K extends DotNestedKeys<SessionState>>(
        path: K,
        value: unknown
    ) => {
        setSessionState(prev => {
            const parts = path.split('.');

            if (parts.length === 1) {
                const [key1] = parts as [keyof SessionState];
                return {
                    ...prev,
                    [key1]: value,
                };
            }

            const [key1, key2] = parts as [keyof SessionState, string];
            const parent = prev[key1];

            if (typeof parent !== "object" || parent === null) {
                console.warn(`setSessionField: '${String(key1)}' is not an object.`);
                return prev;
            }

            return {
                ...prev,
                [key1]: {
                    ...(parent as object),
                    [key2]: value,
                },
            };
        });
    };


    const context: ContextModel = {
        saveSession,
        loadSession,
        sessionState,
        updateSessionState,
        setSessionField
    }

    return (
        <SessionContext.Provider value={context}>
            {children}
        </SessionContext.Provider>
    );
}



